import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import useScheduleCall from '../../../../hooks/calls/useScheduleCall';
import useUpdateScheduledCall from '../../../../hooks/calls/useUpdateScheduledCall';
import './scheduleCallModal.css';

const toDatetimeLocal = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const ScheduleCallModal = ({ isOpen, onClose, conversationId, onCallScheduled, initialCall, onCallUpdated }) => {
  const isEditMode = Boolean(initialCall);
  
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [meetingType, setMeetingType] = useState('google_meet');
  
  const { scheduleCall, loading: creating, error: createError, clearError: clearCreateError } = useScheduleCall();
  const { updateScheduledCall, loading: updating, error: updateError, clearError: clearUpdateError } = useUpdateScheduledCall();
  
  const submitting = creating || updating;
  const error = createError || updateError;
  const clearError = () => { clearCreateError(); clearUpdateError(); };

  useEffect(() => {
    if (isOpen && initialCall) {
      setScheduledDateTime(toDatetimeLocal(initialCall.scheduledDate));
      setDuration(String(initialCall.duration ?? 30));
      setNotes(initialCall.notes ?? '');
      setMeetingType(initialCall.meetingType === 'presential' ? 'presential' : 'google_meet');
    } else if (isOpen && !initialCall) {
      setScheduledDateTime('');
      setDuration('30');
      setNotes('');
      setMeetingType('google_meet');
    }
  }, [isOpen, initialCall]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode) {
        const updatedCall = await updateScheduledCall({
          scheduledCallId: initialCall.id,
          scheduledDateTime,
          duration,
          notes,
          meetingType
        });
        if (onCallUpdated) onCallUpdated(updatedCall);
      } else {
        const scheduledCall = await scheduleCall({
          conversationId,
          scheduledDateTime,
          duration,
          notes,
          meetingType
        });
        if (onCallScheduled) onCallScheduled(scheduledCall);
      }

      setScheduledDateTime('');
      setDuration('30');
      setNotes('');
      setMeetingType('google_meet');
      onClose();
    } catch (err) {
      // Error is already set by the hook
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setScheduledDateTime('');
      setDuration('30');
      setNotes('');
      setMeetingType('google_meet');
      clearError();
      onClose();
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return now.toISOString().slice(0, 16);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel={isEditMode ? 'Actualizar Llamada' : 'Programar Llamada'}
      className="schedule-call-modal"
      overlayClassName="schedule-call-modal-overlay"
      ariaHideApp={false}
    >
      <div className="schedule-call-modal__container">
        <div className="schedule-call-modal__header">
          <h2>{isEditMode ? 'Actualizar Llamada' : 'Programar Llamada'}</h2>
          <button
            className="schedule-call-modal__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="schedule-call-modal__form">
          <div className="schedule-call-modal__field">
            <label>Tipo de reunión</label>
            <div className="schedule-call-modal__radio-group">
              <label className="schedule-call-modal__radio-option">
                <input
                  type="radio"
                  name="meetingType"
                  value="google_meet"
                  checked={meetingType === 'google_meet'}
                  onChange={() => setMeetingType('google_meet')}
                  disabled={submitting}
                />
                <span>Google Meet</span>
              </label>
              <label className="schedule-call-modal__radio-option">
                <input
                  type="radio"
                  name="meetingType"
                  value="presential"
                  checked={meetingType === 'presential'}
                  onChange={() => setMeetingType('presential')}
                  disabled={submitting}
                />
                <span>Presencial</span>
              </label>
            </div>
          </div>

          {meetingType === 'google_meet' && (
            <div className="schedule-call-modal__info-box">
              <strong>ℹ️</strong> Se creará automáticamente una reunión de Google Meet con el enlace de acceso.
            </div>
          )}

          <div className="schedule-call-modal__field">
            <label htmlFor="scheduledDateTime">
              Fecha y Hora <span className="required">*</span>
            </label>
            <input
              id="scheduledDateTime"
              type="datetime-local"
              value={scheduledDateTime}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              min={getMinDateTime()}
              required
              disabled={submitting}
            />
          </div>

          <div className="schedule-call-modal__field">
            <label htmlFor="duration">
              Duración (minutos) <span className="required">*</span>
            </label>
            <input
              id="duration"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              min="15"
              max="480"
              step="15"
              required
              disabled={submitting}
            />
            <small className="schedule-call-modal__hint">
              Duración de la llamada en minutos (15-480)
            </small>
          </div>

          <div className="schedule-call-modal__field">
            <label htmlFor="notes">
              Notas (opcional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agregar notas sobre la llamada..."
              rows="4"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="schedule-call-modal__error">
              {error}
            </div>
          )}

          <div className="schedule-call-modal__actions">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="schedule-call-modal__button schedule-call-modal__button--cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="schedule-call-modal__button schedule-call-modal__button--submit"
            >
              {submitting
                ? (isEditMode ? 'Actualizando...' : (meetingType === 'google_meet' ? 'Creando reunión de Google Meet...' : 'Programando...'))
                : (isEditMode ? 'Actualizar Llamada' : 'Programar Llamada')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ScheduleCallModal;

